import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import { chaves as chamados } from './chamados'
import type { NovaVersaoDeCriterio, VersaoDeCriterio } from '../../types/parametroContratual'

/** As chaves de cache dos critérios contratuais. */
const chaves = {
  tudo: () => ['admin', 'parametros-contratuais'] as const,
}

/**
 * Toda a linha do tempo dos critérios: as versões encerradas, a que vale e as que ainda não valem.
 *
 * Não é a leitura por competência (`/{competencia}`), que devolve só o que vale num mês. A tela de
 * administração precisa justamente do que aquela esconde: uma vigência FUTURA é o motivo de um
 * indicador voltar sem número nos meses anteriores a ela, e sem esta lista essa causa não aparece
 * em tela nenhuma.
 *
 * @returns a consulta do histórico
 */
export function useCriteriosDoContrato() {
  return useQuery({
    queryKey: chaves.tudo(),
    queryFn: async () => (await api.get<VersaoDeCriterio[]>('/admin/parametros-contratuais')).data,
  })
}

/**
 * Registra uma versão nova de um critério.
 *
 * **Não existe edição, e não é limitação.** O banco recusa `UPDATE` e `DELETE` nesta tabela: o
 * critério que sustentou uma glosa assinada não se reescreve por cima, senão a prova de qual
 * critério era desaparece junto. Alterar é acrescentar.
 *
 * @returns a mutação do registro
 */
export function useRegistrarVersaoDeCriterio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (versao: NovaVersaoDeCriterio) =>
      (await api.post<VersaoDeCriterio>('/admin/parametros-contratuais', versao)).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
      // Um critério novo muda o que os indicadores apuram — é para isso que ele existe. Sem estas
      // duas linhas, a apuração da competência e a série continuariam mostrando o número da regra
      // anterior até alguém dar F5, e quem acabou de registrar concluiria que não funcionou.
      void cliente.invalidateQueries({ queryKey: chamados.tudo() })
      void cliente.invalidateQueries({ queryKey: ['indicadores'] })
    },
  })
}
